/*
 * Project: Dynamedics Portal Web
 * Created Date: Tuesday December 5th 2023
 * Author: KasunKarunasekara
 * -----
 * Last Modified: Tuesday December 5th 2023 5:12:30 pm
 * Modified By: KasunKarunasekara at <kkarunasekara@mitrai.com>
 * -----
 * Copyright (c) 2023 Mitra Sparks
 * -----
 * HISTORY:
 */

import settings2Outline from '@iconify/icons-eva/options-2-outline';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
// material
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
  useTheme
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// hooks
import useAuth from '../../hooks/useAuth';
import useIsMountedRef from '../../hooks/useIsMountedRef';
// components
import { MIconButton } from '../../components/@material-extend';
import MenuPopover from '../../components/shared/MenuPopover';
import MyAvatar from '../../components/shared/MyAvatar';
import NavbarConfig from './NavbarConfig';
import Scrollbar from 'src/components/shared/Scrollbar';
import SimpleBar from 'simplebar-react';
import { ROLE_MANAGE_BETA } from 'src/utils/constants';

// ----------------------------------------------------------------------

export default function Menubar() {
  const theme = useTheme();
  const history = useHistory();
  const anchorRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [supplierEventsView, setSupplierEventsView] = useState({});
  //   const [contentHeight, setContentHeight] = useState(0);
  const { MenuLinks } = NavbarConfig(bookmarks, supplierEventsView);
  const coreSection =
    (MenuLinks?.filter((link) => link?.rolePrefix == 'configuration_') ||
      [])[0] || {};

  const commsSection =
    (MenuLinks?.filter((link) => link?.rolePrefix == 'comms_') || [])[0] || {};

  const betaSection =
    (MenuLinks?.filter((link) => link?.rolePrefix == 'beta_') || [])[0] || {};

  const managementLinks = [...coreSection?.items, commsSection?.items];
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleBuy = () => {
    window.open('https://experienz.co.uk/', '_blank');
  };

  //   useEffect(() => {
  //     // Calculate the height of the content dynamically
  //     const content = document.getElementById('menu-content');
  //     if (content) {
  //       setContentHeight(content.scrollHeight);
  //     }
  //   }, [open, managementLinks]);

  const handleLogout = async () => {
    try {
      await logout();
      history.replace('/');
      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout', { variant: 'error' });
    }
  };

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        className={'profile-settings'}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute'
              //   bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72)
            }
          })
        }}
      >
        {/* <MyAvatar
          sx={{ height: 30, width: 30, fontWeight: 300, fontSize: 'small' }}
        /> */}
        <Box
          component={Icon}
          icon={settings2Outline}
          sx={{
            mr: 2,
            color: theme.palette.common.white,
            width: 24,
            height: 24
          }}
        />
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{
          width: '100',
          maxHeight: '70%',
          overflowY: 'auto',
          pb: 1,
          borderRadius: '8px',
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          boxShadow: `rgba(0,0,0,0.2) 5px 5px 15px !important`
        }}
      >
        {/* <Box
            id="menu-content"
            sx={{ maxHeight: contentHeight, overflow: 'auto' }}
          > */}

        <Box sx={{ pl: 3, pt: 2, pr: 3, pb: 1 }}>
          <Typography variant="subtitle2">{coreSection?.subheader}</Typography>
          <Divider
            orientation="horizontal"
            flexItem
            sx={{
              mt: 1,
              height: 1,
              backgroundColor: '#fff'
            }}
          ></Divider>
        </Box>
        {coreSection?.items?.map((option, index) => (
          <Box sx={{ pl: 2, pr: 2 }}>
            <MenuItem
              key={option.title}
              disabled={!(user.roles || []).includes(option.role)}
              to={option.href}
              component={RouterLink}
              onClick={handleClose}
              sx={{ typography: 'body2', py: 1, px: 2.5 }}
            >
              <ListItemIcon>{option.icon}</ListItemIcon>
              <ListItemText disableTypography primary={option.title} />
            </MenuItem>
            {index != coreSection?.items?.length - 1 && (
              <Divider sx={{ my: 1 }} />
            )}
          </Box>
        ))}

        <Box
          sx={{
            pl: 3,
            pt: 2,
            pr: 3,
            pb: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
        >
          <div
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                display: 'inline-block',
                marginRight: '25px'
              }}
            >
              {commsSection?.subheader}
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={handleBuy}
              disabled={
                (commsSection?.items || []).filter((item) =>
                  (user.roles || []).includes(item.role)
                ).length > 0
              }
              sx={{
                backgroundColor: '#1D70B8',
                borderRadius: '25px',
                color: '#fff'
              }}
            >
              Buy
            </Button>
          </div>
          <Divider
            orientation="horizontal"
            flexItem
            sx={{
              mt: 1,
              height: 1,
              backgroundColor: '#fff'
            }}
          ></Divider>
        </Box>
        {commsSection?.items?.map((option, index) => (
          <Box sx={{ pl: 2, pr: 2 }}>
            <MenuItem
              key={option.title}
              disabled={!(user.roles || []).includes(option.role)}
              to={option.href}
              component={RouterLink}
              onClick={handleClose}
              sx={{ typography: 'body2', py: 1, px: 2.5 }}
            >
              <ListItemIcon>{option.icon}</ListItemIcon>
              <ListItemText disableTypography primary={option.title} />
            </MenuItem>
            {index != commsSection?.items?.length - 1 && (
              <Divider sx={{ my: 1 }} />
            )}
          </Box>
        ))}

        {/* beta section */}
        {(user.roles || []).includes(ROLE_MANAGE_BETA) && (
          <>
            <Box sx={{ pl: 3, pt: 2, pr: 3, pb: 1 }}>
              <Typography variant="subtitle2">
                {betaSection?.subheader}
              </Typography>
              <Divider
                orientation="horizontal"
                flexItem
                sx={{
                  mt: 1,
                  height: 1,
                  backgroundColor: '#fff'
                }}
              ></Divider>
            </Box>
            {betaSection?.items?.map((option, index) => (
              <Box sx={{ pl: 2, pr: 2 }}>
                <MenuItem
                  key={option.title}
                  disabled={!(user.roles || []).includes(option.role)}
                  to={option.href}
                  component={RouterLink}
                  onClick={handleClose}
                  sx={{ typography: 'body2', py: 1, px: 2.5 }}
                >
                  <ListItemIcon>{option.icon}</ListItemIcon>
                  <ListItemText disableTypography primary={option.title} />
                </MenuItem>
                {index != betaSection?.items?.length - 1 && (
                  <Divider sx={{ my: 1 }} />
                )}
              </Box>
            ))}
          </>
        )}
        {/* </Box> */}
        <style>
          {`
            ::-webkit-scrollbar {
              width: 6px;
            } 

            ::-webkit-scrollbar-thumb {
              background-color: grey;
              border-radius: 6px;
            }
          `}
        </style>
      </MenuPopover>
    </>
  );
}
